import type { PlgIconName } from "plg/shell/PlgIcon";

export type UnlockKey = "team" | "payroll" | "timeoff" | "holidays" | "performance" | "billing";

interface Unlock {
  label: string;
  icon: PlgIconName;
  title: string;
  line: string;
  /** What the page will hold, each with its own line-art icon. */
  see: [string, PlgIconName][];
  /** A "Coming soon" tag in the sidebar. */
  soon?: boolean;
  /** Replaces the closing line. */
  quiet?: string;
}

// Prototype ZERO (index_last_day.html): the pages a customer gets once they hire, each an empty state for now that
// says what it will hold (`see`).
export const UNLOCKS: Record<UnlockKey, Unlock> = {
  team: {
    label: "Team",
    icon: "users",
    title: "Everyone on your team, in one place",
    line: "Profiles, documents and contracts for every person you hire through Edge.",
    see: [
      ["Signed contract and start date for every person", "contract"],
      ["Their Edge profile, certifications and assessment scores", "ribbon"],
      ["Documents you need for your own records", "folder"],
    ],
  },
  payroll: {
    label: "Payroll",
    icon: "payroll",
    title: "Every payment to your team, in one place",
    line: "Salaries, local taxes and deductions processed every cycle. Nothing for you to run or file.",
    see: [
      ["Payment history and upcoming runs", "clock"],
      ["Local tax and deduction breakdowns", "receipt"],
      ["Bonuses and raises, recorded against each person", "trendUp"],
    ],
  },
  timeoff: {
    label: "Time-off",
    icon: "plane",
    title: "Leave requests and balances, tracked for you",
    line: "Approve in a tap and always know who is away and when.",
    see: [
      ["Requests waiting on you", "inbox"],
      ["Remaining balances per person", "pie"],
      ["A calendar of who is off, and when", "calDays"],
      ["Approve or decline in one tap, from your phone or your inbox", "mobileCheck"],
    ],
  },
  holidays: {
    label: "Holidays",
    icon: "calendar",
    title: "Local public holidays for everyone on your team",
    line: "No diary clashes, and no guessing which days are working days.",
    see: [
      ["Public holidays by country, on your calendar", "globe"],
      ["What each holiday means where they are", "info"],
      ["Sync it with your Google Calendar", "sync"],
    ],
  },
  performance: {
    label: "Performance",
    icon: "target",
    soon: true,
    title: "Set goals and track how they are going",
    line: "Coming soon. You will be able to agree objectives with your team and review them together.",
    see: [
      ["Goals you set together", "target"],
      ["Regular check-ins and notes", "chat"],
      ["A record of what changed and when", "timeline"],
    ],
    quiet: "We are building this now",
  },
  billing: {
    label: "Billing",
    icon: "card",
    title: "Your invoices, in one place",
    line: "No paperwork to chase. Clear billing, available the moment you need it.",
    see: [
      ["Invoices to download", "docDown"],
      ["What you are being charged for, itemised", "listItems"],
      ["A full record of what you have paid", "archive"],
    ],
  },
};

export const UNLOCK_KEYS = Object.keys(UNLOCKS) as UnlockKey[];
export const unlockPath = (key: UnlockKey) => `/hire/unlocks/${key}`;
export const isUnlockKey = (v: string | undefined): v is UnlockKey => !!v && v in UNLOCKS;
